import { Component } from '@theme/component';

function normalizeWholesaleDiscountCopy(root = document.body) {
  if (!root) return;

  const normalizeTextNode = (node) => {
    const original = node.nodeValue ?? '';
    const updated = original
      .replace(/5%\s*(?:\+|MÁS|MAS)\s*5%\s*(?:DESDE|A\s+PARTIR\s+DE)\s*25(?:\s*CAJAS)?/gi, '10% DESDE 25 CAJAS')
      .replace(/9(?:[.,]75)?%\s*(?:DESDE|A\s+PARTIR\s+DE)\s*25(?:\s*CAJAS)?/gi, '10% DESDE 25 CAJAS');

    if (updated !== original) node.nodeValue = updated;
  };

  if (root.nodeType === Node.TEXT_NODE) {
    normalizeTextNode(root);
    return;
  }

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  while (walker.nextNode()) normalizeTextNode(walker.currentNode);
}

function normalizeCurrentContent(root = document.body) {
  window.requestAnimationFrame(() => normalizeWholesaleDiscountCopy(root));
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => normalizeCurrentContent(document.body), { once: true });
} else {
  normalizeCurrentContent(document.body);
}

document.addEventListener('shopify:section:load', (event) => {
  normalizeCurrentContent(event.target);
});

export class AnnouncementBar extends Component {
  #current = 0;
  #interval;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#visibility);
    this.#placeAboveHeader();
    normalizeCurrentContent(this);
    this.play();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.suspend();
    document.removeEventListener('visibilitychange', this.#visibility);
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;
    this.suspend();
    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;
      this.next();
    }, interval);
  }

  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(value) {
    this.toggleAttribute('paused', value);
  }

  suspend = () => {
    clearInterval(this.#interval);
    this.#interval = undefined;
  };

  resume = () => {
    if (!this.autoplay || this.paused) return;
    this.play();
  };

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const value = parseInt(`${this.getAttribute('autoplay')}`, 10);
    return Number.isNaN(value) ? undefined : value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(value) {
    this.#current = value;
    const slides = this.refs.slides ?? [];
    if (!slides.length) return;

    let index = value % slides.length;
    if (index < 0) index += slides.length;

    slides.forEach((slide, position) => {
      slide.setAttribute('aria-hidden', `${position !== index}`);
    });
  }

  #visibility = () => (document.hidden ? this.pause() : this.resume());

  #placeAboveHeader() {
    const section = this.closest('.shopify-section');
    if (!section) return;

    section.dataset.emyPromoTop = 'true';
    Object.assign(section.style, {
      position: 'sticky',
      top: '0',
      zIndex: 'calc(var(--layer-menu-drawer) + 1)',
      backgroundColor: '#0A3AE4',
      boxShadow: '0 3px 12px rgb(0 0 0 / 10%)',
    });

    if (section.querySelector('style[data-emy-promo-style]')) return;

    const style = document.createElement('style');
    style.dataset.emyPromoStyle = 'true';
    style.textContent = `
      [data-emy-promo-top='true'],
      [data-emy-promo-top='true'] .announcement-bar,
      [data-emy-promo-top='true'] .announcement-bar__slider,
      [data-emy-promo-top='true'] .section-background {
        background: #0A3AE4 !important;
      }

      [data-emy-promo-top='true'] .announcement-bar {
        height: 32px;
        min-height: 32px;
        padding-block: 0 !important;
        color: #ffffff !important;
      }

      [data-emy-promo-top='true'] .announcement-bar__slider {
        height: 32px;
      }

      [data-emy-promo-top='true'] .announcement-bar__text,
      [data-emy-promo-top='true'] .announcement-bar__text *,
      [data-emy-promo-top='true'] .slideshow-control,
      [data-emy-promo-top='true'] svg {
        color: #ffffff !important;
        fill: currentColor;
      }

      [data-emy-promo-top='true'] .announcement-bar__text {
        letter-spacing: 0.055em;
        line-height: 1.15;
        text-wrap: balance;
      }

      [data-emy-promo-top='true'] .announcement-bar__slide {
        transition: opacity 0.45s ease-in-out, visibility 0.45s ease-in-out;
      }

      [data-emy-promo-top='true'] .slideshow-control {
        opacity: 0.72;
        transition: opacity 0.2s ease;
      }

      [data-emy-promo-top='true'] .slideshow-control:hover {
        opacity: 1;
        color: #ffffff !important;
      }

      #header-group:has([data-emy-promo-top='true'])
        .header-section:has(> #header-component[sticky]) {
        top: 31px;
      }

      @media (max-width: 749px) {
        [data-emy-promo-top='true'] .announcement-bar,
        [data-emy-promo-top='true'] .announcement-bar__slider {
          height: 30px;
          min-height: 30px;
        }

        [data-emy-promo-top='true'] .announcement-bar__text {
          font-size: 0.625rem !important;
          letter-spacing: 0.035em;
          padding-inline: 4px;
        }

        #header-group:has([data-emy-promo-top='true'])
          .header-section:has(> #header-component[sticky]) {
          top: 29px;
        }
      }
    `;

    section.appendChild(style);
  }
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
